import React, { useState, useEffect } from 'react';
import { Spin, Card, Button, Result } from 'antd';
import axios from 'axios';
import completedIcon from '../../../images/completed.svg';
import failedIcon from '../../../images/failed.svg';
import useLineLiff from 'hooks/LineLiff';
import Header from 'pages/booking/components/Header';
import { decodeBase64Object } from 'shared/common';

import '../../booking/booking.scss';

const LoginRequiredResult = ({ apiResponse }) => {
  const [countdown, setCountdown] = useState(5);
  const redirectUrl = apiResponse?.data?.redirect_url;

  useEffect(() => {
    if (!redirectUrl) return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          window.location.href = redirectUrl;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [redirectUrl]);

  const handleLoginNow = () => {
    if (redirectUrl) {
      window.location.href = redirectUrl;
    }
  };

  return (
    <div className="booking-container">
      <Header />
      <div className="form-container">
        <div className="form-section">
          <Result
            status="warning"
            title="需要重新登入"
            subTitle={
              <div>
                <div style={{ marginBottom: '12px' }}>
                  您的登入狀態已過期，請重新登入以繼續操作
                </div>
                {countdown > 0 && (
                  <div style={{ fontSize: '14px', color: '#666' }}>
                    {countdown} 秒後自動跳轉到登入頁面...
                  </div>
                )}
              </div>
            }
            extra={[
              <Button
                type="primary"
                key="login"
                onClick={handleLoginNow}
                style={{
                  height: '40px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  borderRadius: '8px',
                  marginRight: '8px',
                }}>
                立即重新登入
              </Button>,
            ]}
          />
        </div>
      </div>
    </div>
  );
};

const BookingCancel = () => {
  const { liff, userInfo } = useLineLiff(process.env.REACT_APP_LIFF_ID_PNP);

  const [pnpData, setPnpData] = useState({});
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelResult, setCancelResult] = useState(null);
  const [apiResponse, setApiResponse] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);

  useEffect(() => {
    if (!userInfo) return;

    const queryParams = Object.fromEntries(
      new URLSearchParams(window.location.search)
    );
    // decode base64
    const decodedParams = decodeBase64Object(queryParams);
    setPnpData(decodedParams);

    document.title = `LEXUS | ${decodedParams.title || '取消預約'}`;

    setShowConfirmation(true);
    setPageLoading(false);
  }, [userInfo]);

  const handleConfirmCancel = () => {
    const idToken = liff.getIDToken();
    setShowConfirmation(false);
    handleBookingCancel(pnpData.id, idToken);
  };

  const handleBookingCancel = async (pnpId, idToken) => {
    if (!pnpId || !idToken) {
      setCancelResult('404');
      setApiResponse({
        success: false,
        message: '缺少必要參數，無法取消預約',
      });
      return;
    }

    setCancelLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/pnp/${pnpId}/cancel_booking`,
        {
          line_id_token: idToken,
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
          timeout: 30000,
        }
      );

      const responseData = response.data;
      setApiResponse(responseData);

      if (responseData && responseData.success) {
        setCancelResult('success');
      } else if (
        responseData &&
        responseData.error === 'one_id_refresh_user_failed'
      ) {
        setCancelResult('login_required');
      } else {
        setCancelResult('error');
      }
    } catch (error) {
      console.error('Booking cancel error:', error);
      setCancelResult('error');

      let errorMessage = '取消預約時發生錯誤';

      if (error.response) {
        errorMessage =
          error.response.data?.message ||
          `伺服器錯誤 (${error.response.status})`;
      } else if (error.request) {
        errorMessage = '網路連線錯誤，請檢查您的網路連線';
      } else {
        errorMessage = error.message || '取消預約時發生錯誤';
      }

      setApiResponse({
        success: false,
        message: errorMessage,
      });
    } finally {
      setCancelLoading(false);
    }
  };

  // Loading state when initializing
  if (pageLoading) {
    return (
      <div className="booking-container">
        <Header />
        <div className="form-container">
          <div className="form-section">
            <Card style={{ textAlign: 'center', border: 'none' }}>
              <Spin size="large" />
              <div
                style={{ marginTop: '24px', fontSize: '16px', color: '#666' }}>
                正在載入資料，請稍候...
              </div>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // Confirmation dialog
  if (showConfirmation) {
    return (
      <div className="booking-container">
        <Header />
        <div className="form-container">
          <div className="form-section">
            <Card style={{ border: 'none' }}>
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <div
                  style={{
                    fontSize: '18px',
                    fontWeight: 'bold',
                    marginBottom: '16px',
                    color: '#333',
                  }}>
                  確認取消預約
                </div>
                <div
                  style={{
                    fontSize: '14px',
                    color: '#666',
                    marginBottom: '24px',
                    lineHeight: '1.6',
                  }}>
                  您確定要取消此預約嗎？
                  <br />
                  取消後將無法復原，請謹慎考慮。
                </div>
                {(pnpData.title ||
                  pnpData.work_no ||
                  pnpData.appointed_date) && (
                  <div
                    style={{
                      background: '#f5f5f5',
                      padding: '16px',
                      borderRadius: '8px',
                      marginBottom: '24px',
                      fontSize: '14px',
                      color: '#333',
                      textAlign: 'left',
                    }}>
                    <div style={{ marginBottom: '8px', fontWeight: 'bold' }}>
                      預約資訊
                    </div>
                    <div style={{ marginBottom: '6px' }}>
                      <span style={{ fontWeight: '500' }}>車牌號碼：</span>
                      {pnpData.work_no ? pnpData.work_no : '-'}
                    </div>
                    <div>
                      <span style={{ fontWeight: '500' }}>預約日期：</span>
                      {pnpData.appointed_date ? pnpData.appointed_date : '-'}
                    </div>
                  </div>
                )}
                <div
                  style={{
                    display: 'flex',
                    gap: '12px',
                    justifyContent: 'center',
                  }}>
                  <Button
                    size="large"
                    onClick={() => {
                      setShowConfirmation(false);
                      // Close the LIFF window or go back
                      if (liff && liff.isInClient()) {
                        liff.closeWindow();
                      } else {
                        window.history.back();
                      }
                    }}
                    style={{
                      height: '44px',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      borderRadius: '8px',
                      minWidth: '100px',
                    }}>
                    取消
                  </Button>
                  <Button
                    type="primary"
                    size="large"
                    onClick={handleConfirmCancel}
                    danger
                    style={{
                      height: '44px',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      borderRadius: '8px',
                      minWidth: '100px',
                    }}>
                    確認取消預約
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  if (cancelLoading) {
    return (
      <div className="booking-container">
        <Header />
        <div className="form-container">
          <div className="form-section">
            <Card style={{ textAlign: 'center', border: 'none' }}>
              <Spin size="large" />
              <div
                style={{ marginTop: '24px', fontSize: '16px', color: '#666' }}>
                正在取消您的預約，請勿關閉此頁面...
              </div>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  if (cancelResult === '404') {
    return (
      <div className="booking-container">
        <Header />
        <div className="form-container">
          <div className="form-section">
            <Result status="404" title="404" subTitle="找不到頁面" />
          </div>
        </div>
      </div>
    );
  }

  if (cancelResult === 'login_required') {
    return <LoginRequiredResult apiResponse={apiResponse} />;
  }

  if (cancelResult === 'success') {
    return (
      <div className="booking-container">
        <Header />
        <div className="form-container">
          <div className="form-section">
            <Result
              icon={
                <img
                  src={completedIcon}
                  alt="Success"
                  style={{ width: '64px', height: '64px' }}
                />
              }
              title="預約取消成功"
              subTitle={apiResponse?.message || '您的預約已成功取消'}
            />
          </div>
        </div>
      </div>
    );
  }

  if (cancelResult === 'error') {
    return (
      <div className="booking-container">
        <Header />
        <div className="form-container">
          <div className="form-section">
            <Result
              icon={
                <img
                  src={failedIcon}
                  alt="Failed"
                  style={{ width: '64px', height: '64px' }}
                />
              }
              title="預約取消失敗"
              subTitle={
                apiResponse?.message || '取消預約時發生錯誤，請稍後再試'
              }
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="booking-container">
      <Header />
      <div className="form-container">
        <div className="form-section">
          <Card style={{ textAlign: 'center', border: 'none' }}>
            <Spin size="large" />
            <div style={{ marginTop: '24px', fontSize: '16px', color: '#666' }}>
              正在初始化，請稍候...
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default BookingCancel;
